import { useMemo, useState, useEffect } from "react";
import { PremiumUser } from "@api";
import { Layout } from "@layouts";
import {
  Card as ShadcnCard,
  PieChartComponent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input as SInput,
} from "@shadcn";
import { Button, Spinner, Input } from "@atoms";
import { Card, PageBreadcrumbs } from "@molecules";
import { Receipt as ReceiptIcon, Users } from "lucide-react";
import { lifestyleCategories } from "../categories/categories-data";
import { TransactionListItem } from "./transaction-list-item";
import LifestyleSample1 from "../../../../assets/lifestyle-sample-1.png";
import LifestyleSample2 from "../../../../assets/lifestyle-sample-2.png";
import LifestyleSample3 from "../../../../assets/lifestyle-sample-3.png";
import { useTranslation } from "react-i18next";
import { 
  TimeRangeFilter, 
  LSAMetrics, 
  CategorySpending, 
  SpendingTrend, 
  PendingTransaction, 
  BudgetUtilization, 
  EngagementInsights,
  LifestyleGroup
} from "../../../../types/lifestyle-analytics";
import { LSAGroup, PagePaths } from "../../../../types";

const colors = require("@theme/colors") as Record<string, any>;

const themeColors = require("@theme/colors") as Record<string, any>;
const DASHBOARD_SECTION_HEIGHT_PX = 24 * 16 + 25;
const DASHBOARD_SECTION_HEIGHT = `${DASHBOARD_SECTION_HEIGHT_PX}px`;

export type LifestyleMainPageTemplateProps = {
  navigateToPage: (to: string) => void;
  onReturnPressed: () => void;
  hasLifestyleFund: boolean;
  toggleDesign: () => void;
  // Legacy props for backward compatibility
  employeeSpendingData: {
    category: string;
    amount: number;
    percentage: number;
  }[];
  averageSavings: number;
  topCategories: string[];
  lifestyleGroupName: string;
  coveredUsers: PremiumUser[];
  // New analytics props
  lifestyleGroups: LifestyleGroup[];
  selectedGroupId: string;
  setSelectedGroupId: (id: string) => void;
  selectedGroupDetails: LSAGroup | null;
  timeRange: TimeRangeFilter;
  setTimeRange: (range: TimeRangeFilter) => void;
  lsaMetrics: LSAMetrics;
  categorySpending: CategorySpending[];
  spendingTrends: SpendingTrend[];
  pendingTransactions: PendingTransaction[];
  historyTransactions: PendingTransaction[];
  budgetUtilization: BudgetUtilization;
  engagementInsights: EngagementInsights;
  exportData: (format?: 'csv') => void;
  onApprovePendingTransaction: (transactionId: string) => Promise<void>;
  onRejectPendingTransaction: (transactionId: string) => Promise<void>;
  approvingTransactionIds: Set<string>;
  rejectingTransactionIds: Set<string>;
  creditCardStatus: "valid" | "expired" | "expiringSoon";
  // Loading and error states
  isLoading: boolean;
  isInitialLoad: boolean;
  error: any;
  isDataLoaded: boolean;
  refreshData: (options?: { silent?: boolean }) => void;
  historyLoading: boolean;
};

export const LifestyleMainPageTemplate = ({
  navigateToPage,
  onReturnPressed: _onReturnPressed,
  hasLifestyleFund,
  toggleDesign: _toggleDesign,
  employeeSpendingData,
  averageSavings,
  topCategories,
  lifestyleGroupName,
  coveredUsers,
  // New analytics props
  lifestyleGroups,
  selectedGroupId,
  setSelectedGroupId,
  selectedGroupDetails,
  timeRange,
  setTimeRange,
  lsaMetrics,
  categorySpending,
  spendingTrends,
  pendingTransactions,
  historyTransactions,
  budgetUtilization,
  engagementInsights,
  exportData,
  onApprovePendingTransaction,
  onRejectPendingTransaction,
  approvingTransactionIds,
  rejectingTransactionIds,
  creditCardStatus,
  // Loading and error states
  isLoading,
  isInitialLoad,
  error,
  isDataLoaded,
  refreshData,
  historyLoading
}: LifestyleMainPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const chartColors = useMemo(
    () => [
      themeColors.complimentary.darker,
      themeColors.complimentary.dark,
      themeColors.complimentary.light, 
      "#4299E1",
      "#1A508B",
      "#7a8194",
    ],
    [],
  );
  const CATEGORY_TABLE_ROW_HEIGHT = 56; // pixels
  const CATEGORY_TABLE_HEADER_HEIGHT = 44;
  const CATEGORY_TABLE_MAX_ROWS = 4;
  const getCategoriesTableHeight = (rowCount: number) =>
    CATEGORY_TABLE_HEADER_HEIGHT +
    Math.min(rowCount, CATEGORY_TABLE_MAX_ROWS) * CATEGORY_TABLE_ROW_HEIGHT +
    12;

  const goToPerks = () => navigateToPage(`/dashboard/${PagePaths.perks}`);
  const goToTeam = () => navigateToPage("/dashboard/team");

  const groupOptions = useMemo(
    () =>
      lifestyleGroups.map((group) => ({
        value: group.id,
        label: group.memberCount
          ? t("lifestyleMain.filters.groupOptionLabel", {
              name: group.name,
              count: group.memberCount,
            })
          : group.name,
      })),
    [lifestyleGroups, t],
  );

  const groupSelectOptions = useMemo(
    () => [
      ...groupOptions,
      {
        value: "comingSoon",
        label: t("lifestyleMain.filters.groupOptionComingSoon"),
        isDisabled: true,
      },
    ],
    [groupOptions, t],
  );

  const selectedGroupOption =
    groupSelectOptions.find((option) => option.value === selectedGroupId) ?? null;

  const timeRangeOptions = useMemo(
    () => [
      { value: TimeRangeFilter.LAST_7_DAYS, label: t("lifestyleMain.filters.timeRanges.last7Days") },
      { value: TimeRangeFilter.LAST_30_DAYS, label: t("lifestyleMain.filters.timeRanges.last30Days") },
      { value: TimeRangeFilter.YEAR_TO_DATE, label: t("lifestyleMain.filters.timeRanges.yearToDate") },
      { value: TimeRangeFilter.LAST_12_MONTHS, label: t("lifestyleMain.filters.timeRanges.last12Months") },
    ],
    [t],
  );

  const selectedTimeRangeOption =
    timeRangeOptions.find((option) => option.value === timeRange) ?? null;

  const timeRangeDescription = useMemo(() => {
    switch (timeRange) {
      case TimeRangeFilter.LAST_7_DAYS:
        return t("lifestyleMain.categories.timeRanges.last7Days");
      case TimeRangeFilter.LAST_30_DAYS:
        return t("lifestyleMain.categories.timeRanges.last30Days");
      case TimeRangeFilter.YEAR_TO_DATE:
        return t("lifestyleMain.categories.timeRanges.yearToDate");
      case TimeRangeFilter.LAST_12_MONTHS:
      default:
        return t("lifestyleMain.categories.timeRanges.last12Months");
    }
  }, [timeRange, t]);

  const hasTransactions = pendingTransactions.length > 0;
  const hasSpendingData = categorySpending.reduce((sum, cat) => sum + cat.amount, 0) > 0;
  const hasAnyData = (hasTransactions || hasSpendingData) && engagementInsights?.avgReimbursementAmount > 0;

  const formatCurrency = (value?: number) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return "--";
    }
    return `$${value.toLocaleString()}`;
  };

  const formatPercentage = (value?: number) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return "--";
    }
    return `${value}%`;
  };

  const coveredMembers = (coveredUsers ?? []).map((user, index) => {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
    return {
      id: user?.userId || user?.emailAddress || `member-${index}`,
      name: fullName || user?.emailAddress || t("lifestyleMain.covered.fallbackName"),
      email: user?.emailAddress || "--",
    };
  }).filter((member) => member.name || member.email);

  const [coveredSearchTerm, setCoveredSearchTerm] = useState("");

  const filteredCoveredMembers = useMemo(() => {
    const query = coveredSearchTerm.trim().toLowerCase();
    if (!query) {
      return coveredMembers;
    }
    return coveredMembers.filter((member) => {
      const name = member.name.toLowerCase();
      const email = (member.email ?? "").toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [coveredMembers, coveredSearchTerm]);

  const sortedCategories = useMemo(
    () =>
      [...categorySpending].sort(
        (a, b) => (b?.amount ?? 0) - (a?.amount ?? 0),
      ),
    [categorySpending],
  );

  const coveredCategoryIDs = useMemo(() => {
    const configuredCategories =
      (selectedGroupDetails?.categories ?? [])
        .map((category) => category?.id)
        .filter((id): id is string => Boolean(id));
    if (configuredCategories.length) {
      return configuredCategories;
    }

    return lifestyleCategories.map((category) => category.id);
  }, [selectedGroupDetails]);

  const displayCategories = useMemo(() => {
    return coveredCategoryIDs
      .map((id) => {
        const translationKey = `lifestyleManageGroup.categories.categoryDetails.${id}.name`;
        const translatedName = t(translationKey);
        const categoryName =
          translatedName === translationKey
            ? lifestyleCategories.find((category) => category.id === id)?.name ?? id
            : translatedName;

        return {
          id,
          category: categoryName,
        };
      })
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [coveredCategoryIDs, t]);

  const placeholderAvatars = [
    { initials: "AD", color: "#1a0b0bff" },
    { initials: "JS", color: "#444444" },
    { initials: "LM", color: "#777777" },
  ];

  const summaryBudget = selectedGroupDetails?.yearlySpendingLimit ?? undefined;
  const coverageRate = selectedGroupDetails?.coveragePercentage ?? undefined;
  const totalCategorySpend = sortedCategories.reduce(
    (sum, entry) => sum + (entry?.amount ?? 0),
    0,
  );
  const TOP_CATEGORIES_LIMIT = 5;
  const topChartCategories = sortedCategories.slice(0, TOP_CATEGORIES_LIMIT);
  const remainingCategories = sortedCategories.slice(TOP_CATEGORIES_LIMIT);
  const otherAmount = remainingCategories.reduce((sum, entry) => sum + (entry?.amount ?? 0), 0);
  const chartCategories = otherAmount > 0
    ? [...topChartCategories, { category: t("lifestyleMain.categories.other"), amount: otherAmount }]
    : [...topChartCategories];


  return (
    <Layout
      variant="dashboard"
      header={t("lifestyleMain.header")}
      subheader={
        <div className="flex flex-row justify-between max-w-5xl">
          <div className="page-subtitle mt-2">
            {t("lifestyleMain.subheader")}
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <PageBreadcrumbs
          items={[
            { label: t("lifestyleMain.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("lifestyleMain.breadcrumbs.lifestyleAccount") },
          ]}
        />
        {!hasLifestyleFund ? (
          // View for partners without lifestyle Account
          <div className="flex flex-col pb-6 mt-2">
            {/* Hero Section */}
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl font-semi text-gray-900 mb-2">
                  {t("lifestyleMain.setup.title")}
                </h2>
                <h2 className="text-xl font-semi text-gray-900 mb-5">
                   {t("lifestyleMain.setup.subtitle")}
                </h2>                
                <p className="text-md text-gray-500 mb-6 max-w-2xl mx-auto">
                  {t("lifestyleMain.setup.description")}
                </p>
                <div className="flex justify-center space-x-4">
                  <Button 
                    onClick={() => navigateToPage("/dashboard/lifestyle/create-group")}
                    variant="primary"
                    label={t("lifestyleMain.setup.cta")}
                    color="brand-orange"
                    hoverColor="primary-dark"
                    size="sm"
                  />
                </div>
              </div>
            </div>

            {/* Benefits Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 mt-8">
              <Card 
                variant="benefit"
                icon={LifestyleSample1}
                title={t("lifestyleMain.setup.cards.automaticReimbursements.title")}
                description={t("lifestyleMain.setup.cards.automaticReimbursements.description")}
              />
              <Card 
                variant="benefit"
                icon={LifestyleSample2}
                title={t("lifestyleMain.setup.cards.customCategories.title")}
                description={t("lifestyleMain.setup.cards.customCategories.description")}
              />
              <Card 
                variant="benefit"
                icon={LifestyleSample3}
                title={t("lifestyleMain.setup.cards.insights.title")}
                description={t("lifestyleMain.setup.cards.insights.description")}
              />
            </div>
          </div>
        ) : (
          // View for partners with lifestyle fund
          <div className="flex flex-col pb-8 pt-2 pr-4">
            {/* Loading State */}
            {isLoading && isInitialLoad && (
              <div className="mb-8">
                <div className="text-center text-gray-500">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center">
                    <Spinner variant="large" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t("lifestyleMain.loading.title")}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto px-6">
                    {t("lifestyleMain.loading.description")}
                  </p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className="mb-8">
                <ShadcnCard className="p-12">
                  <div className="text-center text-gray-500">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-500 text-2xl">⚠️</span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t("lifestyleMain.error.title")}</h3>
                    <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                      {t("lifestyleMain.error.description")}
                    </p>
                    <button 
                      onClick={() => refreshData()}
                      className="px-4 py-2 bg-primary-medium text-white rounded-md hover:bg-primary-dark transition-colors text-sm"
                    >
                      {t("lifestyleMain.error.retry")}
                    </button>
                  </div>
                </ShadcnCard>
              </div>
            )}
            
            {/* Main Content */}
            {(!isInitialLoad || !isLoading) && !error && (
            <>
            {/* Group Selector and Time Filter */}
            <div className="mb-5 -mt-1 flex flex-col gap-4">
              <div className="flex w-full max-w-full flex-col gap-4">
                <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-4 xl:flex-nowrap xl:items-center xl:justify-between">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4 w-full">
                    <div className="flex items-center gap-2 w-full md:w-auto">
                      <label className="text-md font-bold text-neutral-darkest">{t("lifestyleMain.filters.groupLabel")}</label>
                      <div
                        className="w-full md:w-[350px]"
                        style={{ minWidth: "280px", width: "100%" }}
                      >
                        <Input
                          key={`group-select-${selectedGroupId}`}
                          name="group"
                          type="groupDropdown"
                          variant="dropdown"
                          options={groupSelectOptions}
                          onChange={(option: any) => {
                            if (!option?.value || option.value === "comingSoon") {
                              return;
                            }
                            setSelectedGroupId(option.value);
                          }}
                          placeholder={selectedGroupOption}
                          value={selectedGroupId}
                          errorMessage={undefined}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 w-full md:w-auto md:ml-2">
                      <label className="text-md font-bold text-neutral-darkest">{t("lifestyleMain.filters.periodLabel")}</label>
                      <div
                        className="w-full md:w-[180px]"
                        style={{ minWidth: "0px", width: "100%" }}
                      >
                        <div className="min-w-[200px] w-full">
                          <Input
                            key={`time-range-select-${timeRange}`}
                            name="timeRange"
                            type="timeRangeDropdown"
                            variant="dropdown"
                            options={timeRangeOptions}
                            onChange={(option: any) => {
                              if (!option?.value) {
                                return;
                              }
                              setTimeRange(option.value as TimeRangeFilter);
                            }}
                            placeholder={selectedTimeRangeOption}
                            value={timeRange}
                            errorMessage={undefined}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full sm:w-auto justify-end sm:ml-auto">
                    <Button
                      variant="primary"
                      label={t("lifestyleMain.filters.manage")}
                      size="sm"
                      color="primary-medium"
                      hoverColor="primary-dark"
                      onClick={() => navigateToPage("/dashboard/lifestyle/manage-group")}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-4.5">
                  <div className="w-full lg:w-1/2 flex flex-col">
                    <div
                      className="rounded-2xl border border-neutral-light bg-white p-4 space-y-4 flex flex-col overflow-hidden"
                      style={{ height: DASHBOARD_SECTION_HEIGHT }}
                    >
                      <div className="flex flex-row items-center justify-between">
                        <div className="flex items-center">
                          <div className="section-title">
                            {t("lifestyleMain.summary.title")}
                          </div>
                        </div>
                        <Button
                          variant="link"
                          label={t("common.manage")}
                          onClick={() => navigateToPage("/dashboard/lifestyle/manage-group")}
                        />
                      </div>
                      <div className="flex flex-row gap-2">
                        <Card
                          variant="metric"
                          label={t("lifestyleMain.summary.yearlyBudget")}
                          value={formatCurrency(summaryBudget)}
                          valueClassName="text-successful-medium"
                          className="p-3 sm:p-4 w-full border border-neutral-light shadow-none"
                        />
                        <Card
                          variant="metric"
                          label={t("lifestyleMain.summary.coverageRate")}
                          value={formatPercentage(coverageRate)}
                          className="p-3 sm:p-4 w-full border border-neutral-light shadow-none"
                        />
                      </div>                 
                      <div className="rounded-2xl border border-neutral-light bg-white w-full mt-2 flex-1 flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-light table-header-background">
                          <p className="text-sm font-semibold text-neutral-darkest">
                            {t("lifestyleMain.summary.coveredCategories")} <span className="text-neutral-mediumDark">{t("lifestyleMain.summary.categoriesCount", { count: displayCategories.length })}</span>
                          </p>
                        </div>
                        {displayCategories.length ? (
                          <div
                            className="overflow-y-auto flex-1 min-h-0"
                            style={{
                              maxHeight: `${getCategoriesTableHeight(displayCategories.length)}px`,
                            }}
                          >
                            <Table className="w-full text-sm">
                              <TableBody>
                                {displayCategories.map((entry) => {
                                  return (
                                    <TableRow key={`category-row-${entry.id}`}>
                                      <TableCell className="px-4 text-sm font-medium text-neutral-darkest">
                                        {entry.category}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="px-4 py-6 text-center metric-subtitle">
                            {t("lifestyleMain.summary.empty")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2 flex flex-col">
                    <div
                      className="rounded-2xl border border-neutral-light bg-white p-4 space-y-4 flex flex-col"
                      style={{ height: DASHBOARD_SECTION_HEIGHT }}
                    >
                      <div className="flex flex-row items-center">
                        <Users size={20} className="text-neutral-darkest" />
                        <div className="section-title ml-2 flex items-center gap-2">
                          <span>{t("lifestyleMain.covered.title")}</span>
                          <span className="text-base text-neutral-mediumDark">
                            {t("lifestyleMain.covered.membersCount", { count: coveredMembers.length })}
                          </span>
                        </div>
                      </div>
                      <div
                        className="rounded-2xl border border-neutral-light bg-white w-full flex-1 flex flex-col overflow-hidden"
                        style={{ height: DASHBOARD_SECTION_HEIGHT }}
                      >
                        {filteredCoveredMembers.length ? (
                          <div className="overflow-y-auto flex-1 min-h-0 h-full">
                            <Table className="w-full text-sm">
                              <TableHeader className="table-header-background sticky top-0 z-10">
                                <TableRow>
                                  <TableHead className="px-4 text-xs font-medium table-header-text uppercase tracking-wide">
                                    {t("lifestyleMain.covered.tableHeaders.name")}
                                  </TableHead>
                                  <TableHead className="px-4 text-xs font-medium table-header-text uppercase tracking-wide">
                                    {t("lifestyleMain.covered.tableHeaders.email")}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {filteredCoveredMembers.map((member) => (
                                  <TableRow key={`covered-member-${member.id}`}>
                                    <TableCell className="px-4 text-sm font-medium text-neutral-darkest">
                                      {member.name}
                                    </TableCell>
                                    <TableCell className="px-4 text-sm text-neutral-darkest">
                                      {member.email}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-4 px-6 py-10 text-center">
                            {!coveredMembers.length ? (
                              <p className="metric-subtitle">
                                {t("lifestyleMain.covered.noSearchResults")}
                              </p>
                            ) : (
                              <>
                                <div className="flex -space-x-3 pb-4">
                                  {placeholderAvatars.map((avatar) => (
                                    <div
                                      key={`placeholder-avatar-${avatar.initials}`}
                                      className="h-12 w-12 rounded-full border-2 border-white flex items-center justify-center text-sm font-semibold text-neutral-lightest shadow-sm"
                                      style={{ backgroundColor: avatar.color }}
                                    >
                                      {avatar.initials}
                                    </div>
                                  ))}
                                </div>
                                <div className="space-y-1">
                                  <p className="text-base font-semibold text-neutral-darkest">
                                    {t("lifestyleMain.covered.noMembersTitle")}
                                  </p>
                                  <p className="metric-subtitle max-w-xs">
                                    {t("lifestyleMain.covered.noMembersSubtitle")}
                                  </p>
                                </div>
                                <div>
                                  <Button
                                    onClick={goToTeam}
                                    variant="secondary"
                                    label={t("lifestyleMain.covered.invite")}
                                    autosize
                                    color="#FF7A00"
                                    hoverColor="#E56B00"
                                    textColor="#FFFFFF"
                                  />
                                </div>
                             </>
                           )}
                         </div>
                       )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-5">
              <div className="rounded-2xl border border-neutral-light bg-white p-6">
                <div className="flex flex-row items-center mb-4">
                  <div className="section-title">
                    {t("lifestyleMain.performance.title")}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card
                    variant="metric"
                    label={t("lifestyleMain.performance.totalRequested")}
                    value={formatCurrency(Number((lsaMetrics?.totalReimbursed ?? 0).toFixed(2)))}
                    valueClassName="text-successful-medium"
                  />
                  <Card
                    variant="metric"
                    label={t("lifestyleMain.performance.averagePerMember")}
                    value={formatCurrency(Number((lsaMetrics?.avgPerMember ?? 0).toFixed(2)))}
                  />
                  <Card
                    variant="metric"
                    label={t("lifestyleMain.performance.participationRate")}
                    value={formatPercentage(Math.round(lsaMetrics?.participationRate ?? 0))}
                  />
                </div>
              </div>
            </div>


            {/* Pending Transactions & History */}
            <div className="mb-3 flex flex-col lg:flex-row gap-4 lg:gap-4.5">
              <ShadcnCard className="p-6 h-96 w-full lg:w-1/2">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center">
                    <div className="section-title flex items-center gap-2">
                      <span>{t("lifestyleMain.pending.title")}</span>
                      <span className="text-base text-neutral-mediumDark">
                        ({pendingTransactions.length})
                      </span>
                    </div>
                  </div>
                </div>
                  <div className="space-y-4 overflow-y-auto max-h-80">
                    {pendingTransactions && pendingTransactions.length > 0 ? (
                      pendingTransactions.map((transaction) => {
                        const isApproving = approvingTransactionIds.has(transaction.id);
                        const isRejecting = rejectingTransactionIds.has(transaction.id);
                        return (
                          <TransactionListItem
                            key={transaction.id}
                            memberName={transaction.memberName}
                            companyName={transaction.companyName}
                            submittedDate={transaction.submittedDate}
                            amount={transaction.amount}
                            receiptUrl={transaction.receiptUrl}
                            onApprove={() => onApprovePendingTransaction(transaction.id)}
                            onReject={() => onRejectPendingTransaction(transaction.id)}
                            isApproving={isApproving}
                            isRejecting={isRejecting}
                            disableApprove={creditCardStatus === "expired"}
                            className="p-4 bg-gray-50 rounded-lg"
                          />
                        );
                      })
                    ) : (
                      <div className="text-center pt-16 text-gray-500">
                        <ReceiptIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm font-medium">{t("lifestyleMain.pending.emptyTitle")}</p>
                        <p className="text-xs mt-1">{t("lifestyleMain.pending.emptySubtitle")}</p>
                      </div>
                    )}
                  </div>
                </ShadcnCard>

              <ShadcnCard className="p-6 h-96 w-full lg:w-1/2">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center">
                    <div className="section-title flex items-center gap-2">
                      <span>{t("lifestyleMain.history.title")}</span>
                      <span className="text-base text-neutral-mediumDark">
                        ({historyTransactions.length})
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="link"
                      label={t("common.viewAll")}
                      onClick={() => navigateToPage("/dashboard/lifestyle/transactions")}
                    />
                  </div>
                </div>
                <div className="space-y-4 overflow-y-auto max-h-80">
                  {historyLoading ? (
                    <div className="flex items-center justify-center h-48">
                      <Spinner variant="large" />
                    </div>
                  ) : historyTransactions && historyTransactions.length > 0 ? (
                    historyTransactions.map((transaction) => {
                      const statusKey = transaction.status
                        ? `lifestyleTransactions.statusTags.${transaction.status}`
                        : "";
                      const capitalizedStatus =
                        transaction.status?.charAt(0).toUpperCase() + transaction.status?.slice(1);
                      const statusLabel = transaction.status
                        ? t(statusKey, { defaultValue: capitalizedStatus })
                        : t("lifestyleMain.history.statusFallback");
                      return (
                        <TransactionListItem
                          key={`history-${transaction.id}`}
                          memberName={transaction.memberName}
                          companyName={transaction.companyName}
                          submittedDate={transaction.submittedDate}
                          amount={transaction.amount}
                          statusLabel={statusLabel || t("lifestyleMain.history.statusFallback")}
                          statusVariant="badge"
                          className="p-4 bg-gray-50 rounded-lg"
                        />
                      );
                    })
                  ) : (
                    <div className="text-center pt-16 text-gray-500">
                      <ReceiptIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm font-medium">{t("lifestyleMain.history.emptyTitle")}</p>
                      <p className="text-xs mt-1">{t("lifestyleMain.history.emptySubtitle")}</p>
                    </div>
                  )}
                </div>
              </ShadcnCard>
            </div>

            {hasAnyData && // hide the rest of the page when there is no activity yet
            <>
              <div className="flex flex-col lg:flex-row gap-8">
              </div>

              {/* Category Breakdown with Most Popular Categories */}
              <div className="mb-4 pt-2">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">

                  {/* Most Popular Categories */}
                  <div className="flex flex-col">
                    <ShadcnCard className="p-6 h-full">
                      <div className="space-y-1">
                        <div className="flex flex-row items-center">
                          <div className="section-title">
                            {t("lifestyleMain.categories.popularTitle")}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          {t("lifestyleMain.categories.popularDescription", { timeRange: timeRangeDescription })}
                          </p>
                        </div>
                      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1">
                        <div className="space-y-3 flex-1 overflow-y-auto lg:max-h-80">
                          {engagementInsights.mostPopularCategories.map((category, index) => {
                            const categoryData = lifestyleCategories.find(cat => cat.name === category.category);
                            return (
                              <div key={index} className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <span className="text-lg">{categoryData?.icon || "📊"}</span>
                                  <div>
                                    <div className="text-md font-medium text-gray-900">{category.category}</div>
                                    <div className="text-xs text-gray-600">${category.amount.toLocaleString()}</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex lg:items-start">
                          <Card
                            variant="metric"
                            label={t("lifestyleMain.categories.avgReimbursement")}
                            value={formatCurrency(Number(engagementInsights.avgReimbursementAmount.toFixed(2)))}
                            className="border border-neutral-light shadow-none w-full self-start"
                            valueClassName="text-2xl font-bold text-neutral-darkest"
                          />
                        </div>
                      </div>
                    </ShadcnCard>
                  </div>

                  {/* Spending by Category Pie Chart */}
                  <div className="flex flex-col">
                    <div className="rounded-2xl border border-neutral-light bg-white px-5 pt-5 pb-5 flex flex-col items-start">
                      <div className="flex flex-row items-center">
                        <div className="section-title flex items-center gap-2">
                          <span>{t("lifestyleMain.categories.spendingByCategoryTitle")}</span>
                          <span className="text-base font-normal text-neutral-mediumDark">
                            {t("lifestyleMain.categories.topLabel")}
                          </span>
                        </div>
                      </div>
                      <PieChartComponent 
                        data={chartCategories.map((item, index) => ({
                          name: item.category,
                          value: item.amount,
                          color: chartColors[index % chartColors.length]
                        }))}
                        height={290}
                      includeLegend={false}
                      hideHeader
                      hideLabels
                        cardClassName="shadow-none border-0 w-full"
                        contentClassName="flex-1 flex flex-col justify-between w-full"
                      />
                      {chartCategories.length ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl mx-auto pb-2">
                          {chartCategories.map((item, index) => {
                            const color = chartColors[index % chartColors.length];
                            const total = totalCategorySpend || 1;
                            const share = ((item.amount / total) * 100).toFixed(1);

                            return (
                              <div
                                key={`category-legend-${item.category}`}
                                className="flex items-center justify-between px-1 py-1"
                              >
                                <div className="flex items-center gap-3">
                                  <span
                                    className="h-3 w-3 rounded-full"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="text-sm font-medium text-neutral-darkest">
                                    {item.category}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </>
            }
            </>
            )}

          </div>
        )}
      </div>
    </Layout>
  );
}; 
